import type { ReactNode } from "react";
import { cn } from "../../lib/cn";
import { useHoldToComplete } from "../hold-to-complete";

export function HoldPrimaryButton({
  label,
  onPrimary,
  variant = "outline",
  surfaceBg,
  surfaceInk,
  hint = false,
}: {
  label: ReactNode;
  onPrimary?: () => void;
  variant?: "outline" | "filled";
  /** Palette override for dark tier surfaces: the pill background (paper). */
  surfaceBg?: string;
  /** Palette override for dark tier surfaces: the pill ink (deep surface tone). */
  surfaceInk?: string;
  /**
   * Teach the gesture. Set until the member has completed one hold: the label
   * says to hold, and the ring rehearses the fill on a loop. A member who taps
   * and sees nothing happen has no way to discover the gesture otherwise, and
   * reads the card as broken.
   */
  hint?: boolean;
}) {
  const { progress, holding, bind } = useHoldToComplete({
    done: false,
    onComplete: () => onPrimary?.(),
  });
  const ringSize = 22;
  const ringStroke = 2.5;
  const radius = (ringSize - 6) / 2;
  const circumference = 2 * Math.PI * radius;
  const dashoffset = circumference * (1 - progress);
  const done = progress >= 1;
  const filled = variant === "filled";
  const custom = Boolean(surfaceBg && surfaceInk);
  const teal = "var(--color-teal-700, #0b6c79)";
  const tealDeep = "var(--color-teal-800, #075560)";
  const track = custom
    ? `color-mix(in srgb, ${surfaceInk} 28%, transparent)`
    : filled
      ? "color-mix(in srgb, white 35%, transparent)"
      : "var(--color-mist-dark, #c9d2cf)";
  const background = custom ? (surfaceBg as string) : filled ? teal : "var(--color-paper, #ffffff)";
  const ink = custom ? (surfaceInk as string) : filled ? "#ffffff" : done ? tealDeep : teal;
  const ringColor = custom
    ? (surfaceInk as string)
    : filled
      ? "color-mix(in srgb, white 85%, transparent)"
      : teal;
  const doneDotBg = custom ? (surfaceInk as string) : filled ? "#ffffff" : teal;
  // Not merely "not holding": releasing part-way leaves the real ring decaying
  // back to empty, and the rehearsal restarting over the top of it puts two
  // arcs on one circle, which reads as a glitch rather than a hint.
  const showHint = hint && !holding && !done && progress === 0;
  /*
   * One instruction, used for both names. The button already announced "Hold
   * to mark as done" to a screen reader while showing something else, and the
   * two were built separately, so a label that already said "Hold" came out as
   * "Hold to hold to finish" in the assistive name only. Deriving both from
   * here keeps the visible and accessible names in agreement by construction,
   * which is also what WCAG's Label in Name asks for.
   *
   * Only a verb phrase survives the prefix, which is all this button is ever
   * given: it is rendered for microsteps alone, whose action reads "Mark as
   * done".
   */
  const holdInstruction =
    typeof label === "string"
      ? /^hold\b/i.test(label)
        ? label
        : `Hold to ${label.charAt(0).toLowerCase()}${label.slice(1)}`
      : "Hold to complete";
  const hintLabel = showHint ? holdInstruction : label;
  const doneCheckStroke = custom ? (surfaceBg as string) : filled ? teal : "#ffffff";

  return (
    <button
      type="button"
      // Owns its own hold audio (via useHoldToComplete); opts out of the default press cue.
      data-silent=""
      {...bind}
      aria-pressed={done}
      aria-label={holdInstruction}
      className={cn(
        "inline-flex items-center gap-2 whitespace-nowrap rounded-full px-4 py-2 font-sans text-sm font-semibold tracking-normal transition-transform duration-150 ease-[var(--ease-out-cubic)] select-none touch-none",
        filled
          ? "shadow-[0_2px_8px_-2px_color-mix(in_srgb,var(--color-navy-deep)_45%,transparent)] hover:-translate-y-px hover:shadow-[0_4px_12px_-2px_color-mix(in_srgb,var(--color-navy-deep)_55%,transparent)]"
          : "shadow-[0_2px_8px_-2px_color-mix(in_srgb,var(--color-navy-deep)_25%,transparent)] hover:-translate-y-px hover:shadow-[0_4px_12px_-2px_color-mix(in_srgb,var(--color-navy-deep)_35%,transparent)]",
        holding ? "scale-[0.98]" : "",
      )}
      style={{
        background: done && filled && !custom ? tealDeep : background,
        color: ink,
        border:
          filled || custom
            ? "1px solid transparent"
            : `1px solid ${done ? teal : "color-mix(in srgb, var(--color-teal-700) 35%, transparent)"}`,
        WebkitTapHighlightColor: "transparent",
      }}
    >
      {/* The visible name converges with the accessible one the button already
          carried ("Hold to mark as done"), rather than inventing a second
          vocabulary for the same instruction. */}
      <span>{hintLabel}</span>
      <span
        aria-hidden="true"
        className="relative inline-grid place-items-center"
        style={{ width: ringSize, height: ringSize }}
      >
        <span
          style={{
            position: "absolute",
            inset: 2,
            borderRadius: "50%",
            background: doneDotBg,
            transform: done ? "scale(1)" : "scale(0.6)",
            opacity: done ? 1 : 0,
            transition: "transform .35s cubic-bezier(.34,1.56,.64,1), opacity .2s ease",
          }}
        />
        <svg
          width={ringSize}
          height={ringSize}
          viewBox={`0 0 ${ringSize} ${ringSize}`}
          style={{ position: "absolute", inset: 0 }}
          aria-hidden="true"
        >
          <circle
            cx={ringSize / 2}
            cy={ringSize / 2}
            r={radius}
            fill="none"
            stroke={track}
            strokeWidth={ringStroke}
          />
          {/* The rehearsal rides the live stroke's own geometry, one layer
              beneath it, so the hint and the real thing are the same object
              rather than an ornament placed near it. */}
          {showHint ? (
            <circle
              className="hold-hint-arc"
              cx={ringSize / 2}
              cy={ringSize / 2}
              r={radius}
              fill="none"
              stroke={ringColor}
              strokeWidth={ringStroke}
              strokeLinecap="round"
              strokeDasharray={circumference}
              transform={`rotate(-90 ${ringSize / 2} ${ringSize / 2})`}
              style={
                {
                  "--hint-c": circumference,
                  // Reads as a rehearsal, not as progress already underway:
                  // at full strength a member thinks something is loading.
                  opacity: 0.45,
                } as React.CSSProperties
              }
            />
          ) : null}
          <circle
            cx={ringSize / 2}
            cy={ringSize / 2}
            r={radius}
            fill="none"
            stroke={ringColor}
            strokeWidth={ringStroke}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={dashoffset}
            transform={`rotate(-90 ${ringSize / 2} ${ringSize / 2})`}
          />
        </svg>
        <svg
          viewBox="0 0 20 20"
          fill="none"
          stroke={doneCheckStroke}
          strokeWidth={2.6}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          style={{
            position: "relative",
            width: ringSize * 0.55,
            height: ringSize * 0.55,
            transform: done ? "scale(1)" : "scale(0.5)",
            opacity: done ? 1 : 0,
            transition: "transform .35s cubic-bezier(.34,1.56,.64,1), opacity .15s ease",
          }}
        >
          <path d="M4 10.5 L8.5 15 L16 6.5" />
        </svg>
      </span>
    </button>
  );
}
